import orderBy from 'lodash/orderBy';

import { CustomProperty, CustomPropertyType } from '@evidentid/tprm-portal-lib/models/dashboard';

import { getRequirementDisplayName, RequirementLabels } from '@/components/migrated-compliance-section/migratedCompliance';
import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
import { FieldTypeFormat } from '@/modules/dynamic-form/models/FieldTypeFormat.model';
import { FieldValidator } from '@/modules/dynamic-form/models/FieldValidator.model';
import { TypeDefinitionEnum } from '@/modules/dynamic-form/models/TypeDefinition.model';
import { UnifiedFieldRecord, UnifiedFieldRecordKey } from '@/modules/dynamic-form/models/UnifiedFieldRecord.model';
import { toFieldPath } from '@/modules/dynamic-form/utils/to-field-path/toFieldPath';
import { createRequiredValidator } from '@/modules/dynamic-form/validations';
import { createDateValidators } from '@/modules/dynamic-form/validations/date/createDateValidators';
import { COUNTRIES_ISO_3166_1 } from '@/modules/entity-management/constants/countryCodes';

function createEmailValidator(): FieldValidator {
    return {
        name: 'required' as any,
        validate: (val: any) => {
            if (!val) {
                return true;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(val) || 'Must be a valid email address';
        },
    };
}

function createNoWhitespaceOnlyValidator(): FieldValidator {
    return {
        name: 'required' as any,
        validate: (val: any) => {
            if (!val) {
                return true;
            }
            return /\S/.test(val) || 'Must not contain only whitespace';
        },
    };
}

function buildStringItemsMeta(label: string, fieldPath: FieldPath): FieldMeta {
    return {
        path: fieldPath,
        label,
        type: { $type: 'String' },
        required: false,
        defaultValue: '',
    };
}

function buildAddressProperties(): Record<string, FieldMeta> {
    const countryValues = COUNTRIES_ISO_3166_1.map((c) => ({
        value: c.code,
        label: (c as any).label || c.name,
    }));

    return {
        country: {
            path: toFieldPath('country'),
            label: 'Country',
            type: {
                $type: 'Enum',
                values: countryValues,
                allowCustomValues: false,
            } as TypeDefinitionEnum,
            required: false,
            defaultValue: 'US',
        },
        street: {
            path: toFieldPath('street'),
            label: 'Street Address',
            type: { $type: 'String' },
            required: false,
            defaultValue: '',
        },
        aptSuiteOrUnit: {
            path: toFieldPath('aptSuiteOrUnit'),
            label: 'Apt, Suite or Unit',
            type: { $type: 'String' },
            required: false,
            defaultValue: '',
        },
        city: {
            path: toFieldPath('city'),
            label: 'City',
            type: { $type: 'String' },
            required: false,
            defaultValue: '',
        },
        state: {
            path: toFieldPath('state'),
            label: 'State',
            type: { $type: 'String' },
            required: false,
            defaultValue: '',
        },
        zipCode: {
            path: toFieldPath('zipCode'),
            label: 'Zip Code',
            type: { $type: 'String' },
            required: false,
            defaultValue: '',
        },
    };
}

function buildCollateralItemMeta(): FieldMeta {
    return {
        path: toFieldPath('item'),
        label: 'Collateral',
        type: { $type: 'Object' } as any,
        required: false,
        defaultValue: {},
        properties: {
            description: {
                path: toFieldPath('description'),
                label: 'Description',
                type: { $type: 'String' },
                required: true,
                defaultValue: '',
            },
            category: {
                path: toFieldPath('category'),
                label: 'Category',
                type: { $type: 'String' },
                required: true,
                defaultValue: '',
            },
            uniqueIdentifier: {
                path: toFieldPath('uniqueIdentifier'),
                label: 'Unique Identifier',
                type: { $type: 'String' },
                required: false,
                defaultValue: '',
            },
            limitRequired: {
                path: toFieldPath('limitRequired'),
                label: 'Limit Required',
                type: { $type: 'Float' },
                required: true,
                defaultValue: null,
            },
            maximumDeductible: {
                path: toFieldPath('maximumDeductible'),
                label: 'Maximum Deductible',
                type: { $type: 'Float' },
                required: false,
                defaultValue: null,
            },
        },
    };
}

function buildCustomPropertyFieldMeta(path: FieldPath, prop: CustomProperty): FieldMeta | null {
    switch (prop.type) {
        case CustomPropertyType.string:
            return {
                path,
                label: prop.name,
                type: { $type: 'String' },
                required: prop.required,
                defaultValue: '',
            };
        case CustomPropertyType.integer:
            return {
                path,
                label: prop.name,
                type: { $type: 'Int' },
                required: false,
                defaultValue: null,
            };
        case CustomPropertyType.number:
            return {
                path,
                label: prop.name,
                type: { $type: 'Float' },
                required: false,
                defaultValue: null,
            };
        case CustomPropertyType.boolean:
            return {
                path,
                label: prop.name,
                type: { $type: 'Boolean' },
                required: false,
                defaultValue: null,
            };
        case CustomPropertyType.listString:
            return {
                path,
                label: prop.name,
                type: { $type: 'List', elementType: { $type: 'String' }, nonEmpty: prop.required },
                required: prop.required,
                defaultValue: [],
                items: buildStringItemsMeta(prop.name, path),
            };
        case CustomPropertyType.listEmail:
            return {
                path,
                label: prop.name,
                type: { $type: 'List', elementType: { $type: 'String' } },
                required: false,
                defaultValue: [],
                items: buildStringItemsMeta(prop.name, path),
            };
        case CustomPropertyType.address:
            return {
                path,
                label: prop.name,
                type: { $type: 'Address' },
                required: false,
                defaultValue: null,
                properties: buildAddressProperties(),
            };
        case CustomPropertyType.collaterals:
            return {
                path,
                label: prop.name,
                type: { $type: 'Collateral' } as any,
                required: false,
                defaultValue: [],
                items: buildCollateralItemMeta(),
            };
        case CustomPropertyType.credentialTypes: {
            const schema = prop.schema as any;
            const enumValues: string[] = schema?.enum || schema?.items?.enum || [];
            const enumLabels: string[] = schema?.enumLabels || schema?.items?.enumLabels || enumValues;
            return {
                path,
                label: prop.name,
                type: {
                    $type: 'Enum',
                    values: enumValues.map((val: string, idx: number) => ({
                        value: val,
                        label: enumLabels[idx] || val,
                    })),
                    allowCustomValues: false,
                } as TypeDefinitionEnum,
                required: prop.required,
                defaultValue: '',
            };
        }
        default:
            return null;
    }
}

export function buildContractExtractionEntityFieldMeta(
    customProperties: CustomProperty[],
    requirementTypes: string[],
    requirementLabels?: RequirementLabels,
): FieldMeta[] {
    // Order matches AddIndividualEntityModal: displayName → legalName → doingBusinessAs →
    // contactEmail → contactName → contactPhoneNumber → address → insuredFields → exceptions
    const fields: FieldMeta[] = [
        {
            path: toFieldPath('displayName'),
            label: 'Display Name',
            type: { $type: 'String', minLength: 1 },
            required: true,
            defaultValue: '',
        },
        {
            path: toFieldPath('legalName'),
            label: 'Legal Name',
            type: { $type: 'String' },
            required: false,
            defaultValue: '',
        },
        {
            path: toFieldPath('doingBusinessAs'),
            label: 'DBA Name(s)',
            type: { $type: 'List', elementType: { $type: 'String' } },
            required: false,
            defaultValue: [],
            items: buildStringItemsMeta('DBA Name', toFieldPath('doingBusinessAs')),
        },
        {
            path: toFieldPath('contactEmail'),
            label: 'Primary Contact Email',
            type: { $type: 'String' },
            required: true,
            defaultValue: '',
        },
        {
            path: toFieldPath('contactName'),
            label: 'Primary Contact Name',
            type: { $type: 'String' },
            required: false,
            defaultValue: '',
        },
        {
            path: toFieldPath('contactPhoneNumber'),
            label: 'Primary Contact Phone Number',
            type: { $type: 'String', format: FieldTypeFormat.INTERNATIONAL_PHONE_NUMBER },
            required: false,
            defaultValue: '',
        },
        {
            path: toFieldPath('address'),
            label: 'Address',
            type: { $type: 'Address' },
            required: false,
            defaultValue: null,
            properties: buildAddressProperties(),
        },
    ];

    const sortedCustomProperties = orderBy(
        customProperties,
        [ 'required', (prop) => prop.name.toLowerCase() ],
        [ 'desc', 'asc' ],
    );
    for (const prop of sortedCustomProperties) {
        const path = toFieldPath(`insuredFields.${prop.key}`);
        const fieldMeta = buildCustomPropertyFieldMeta(path, prop);
        if (fieldMeta) {
            fields.push(fieldMeta);
        }
    }

    for (const reqType of requirementTypes) {
        fields.push({
            path: toFieldPath(`exceptions.${reqType}`),
            label: getRequirementDisplayName(reqType, requirementLabels),
            type: { $type: 'Date' },
            required: false,
            defaultValue: '',
        });
    }

    return fields;
}

export function buildContractExtractionEntityFieldValidators(
    fields: FieldMeta[],
): UnifiedFieldRecord<FieldValidator[]> {
    const result: UnifiedFieldRecord<FieldValidator[]> = {} as UnifiedFieldRecord<FieldValidator[]>;

    for (const field of fields) {
        const validators: FieldValidator[] = [];

        if (field.path === toFieldPath('displayName') as FieldPath) {
            validators.push(createRequiredValidator());
            validators.push(createNoWhitespaceOnlyValidator());
        } else if (field.path === toFieldPath('contactEmail') as FieldPath) {
            validators.push(createRequiredValidator());
            validators.push(createEmailValidator());
        } else if (field.required) {
            validators.push(createRequiredValidator());
        } else if (field.path.startsWith('exceptions.')) {
            // A date that cannot be read must not be saved, or sent: the badge and summary would
            // say nothing is being migrated while it goes to the API.
            validators.push(...createDateValidators(field.label));
        }

        if (validators.length > 0) {
            (result as any)[field.path] = { $self: validators } as Record<UnifiedFieldRecordKey, FieldValidator[]>;
        }
    }

    return result;
}
